/**
 * AtlasPerfTourModule — the live PERFORMANCE TOUR (2026-09-17).
 *
 * "Build a live probe that the user runs and it measures every single metric
 * and stores it in a debug table in spacetimedb … teleport to a settlement and
 * walk for 30 seconds, a forest, an ocean, a sand desert with a sandstorm …
 * repeat on different graphics levels … triangle count and where it comes
 * from."
 *
 * THE TOUR. For every tier asked for (all six by default), in ladder order:
 * switch the tier (`PostModule.setTier` — the post chain rebuilds, warm assets
 * are evicted), let it settle, then four LEGS — the nearest CITY's centre, the
 * nearest FOREST stand, the OCEAN SHORE, and a DUNE DESERT under a pinned
 * SANDSTORM — each an admin `debugTeleport`, a wait for the terrain and the
 * settlement to finish streaming, and `legSeconds` (30) of WALKING: the input
 * module's tour drive (`setAutoWalk` — a product-side stick merged like the
 * gamepad, never a synthetic key) walks forward while the yaw sweeps at
 * 0.15 rad/s, so the camera faces every direction of the spot.
 *
 * THE METRICS, once a second (`perf_sample`): fps (frames in the second),
 * frame-time p50 / p95 / max over the second's frames (wall clock, not the
 * engine's clamped dt), draw calls and triangles (three's per-frame
 * `info.render`), the pipeline count (`__pipelines`), the JS heap
 * (`performance.memory`, Chromium), the GPU frame time (the PerfHUD's resolved
 * timestamp ring when the backend has `timestamp-query`), Long Animation
 * Frames in the second (`__loaf`), the position, and every fifth second the
 * TRIANGLE CENSUS (`__triangleCensus`: a frame's triangles by object family —
 * where the triangles come from). Per leg (`perf_leg`): the aggregates over
 * every frame of the walk, the census at the end, and the extras — the
 * foliage census, the settlement census, the terrain readiness, the draw
 * census top rows, the weather kind. Per run (`perf_run`): who, when, the
 * backend, the tiers. Everything lands through debug-gated reducers with
 * CLIENT-minted keys (`runKey` / `legKey`), so a lost response never stalls
 * the walk. Read it back with `npm run verify:perf-tour-report`.
 *
 * Levers: the Probes window's "Perf tour" card (backtick menu → Probes…), or
 * `__perfTour({ tiers, legSeconds, legs, label })` → a promise of the run
 * summary; `__perfTour.status()`; `__perfTour.abort()`. Commons only (the
 * teleports are Commons finders). The spots are resolved ONCE from the start
 * position so every tier walks the same four places.
 */
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { settlementSites } from '@shared/settlements.js';
import { TIER_ORDER, TIER_LABEL, activeTier, activePreset, setAutoTune, type Tier } from '../lib/quality_tiers.js';
import type { AtlasDebugMenuModule } from './AtlasDebugMenuModule.js';
import type { AtlasInputModule } from './AtlasInputModule.js';
import type { PostModule } from './PostModule.js';
import type { WeatherModule } from './WeatherModule.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import type { CommonsSettlementsModule } from './CommonsSettlementsModule.js';
import type { FeatureKind, FeatureSpot } from '../lib/terrain_features.js';

export type LegKind = 'settlement' | 'forest' | 'ocean' | 'desert_storm';
export const LEG_KINDS: readonly LegKind[] = ['settlement', 'forest', 'ocean', 'desert_storm'];
/** The feature finder behind each non-settlement leg. */
const LEG_FEATURE: Record<Exclude<LegKind, 'settlement'>, FeatureKind> = { forest: 'forest', ocean: 'shore', desert_storm: 'dune_desert' };

export interface TourOptions {
  tiers?: Tier[];
  legSeconds?: number;
  legs?: LegKind[];
  label?: string;
  /** After a tier switch: the post chain rebuilds and the warm assets re-load. */
  settleMs?: number;
  /** The cap on the terrain / settlement streaming wait after a teleport. */
  loadMaxMs?: number;
  /** The yaw sweep while walking (rad/s). */
  turnRadPerS?: number;
}

interface Spot { x: number; z: number; label: string }
interface Sample {
  t: number; fps: number; frameMsP50: number; frameMsP95: number; frameMsMax: number; drawCalls: number; triangles: number; pipelines: number;
  heapMb: number; gpuMs: number; loaf: number; x: number; z: number;
}
export interface LegSummary {
  legKey: string; tier: Tier; kind: LegKind; spot: string; status: 'done' | 'skipped'; notes: string; samples: number;
  fpsAvg: number; fpsP05: number; frameMsP50: number; frameMsP95: number; frameMsP99: number; frameMsMax: number;
  drawCallsAvg: number; trianglesAvg: number; trianglesMax: number; pipelines: number; heapMbAvg: number; heapMbMax: number;
  loafCount: number; loafMsTotal: number; gpuMsAvg: number; censusTop: Array<{ name: string; tris: number }>;
}
export interface TourStatus {
  running: boolean; runKey: string; label: string; tier: Tier | ''; leg: LegKind | ''; phase: string; tierIndex: number; tierCount: number;
  legIndex: number; legCount: number; samples: number; lastSample: Sample | null; legs: LegSummary[]; error: string; done: boolean; startedAt: number;
}

type Reducers = {
  perfRunStart(a: { runKey: string; label: string; backend: string; userAgent: string; tiers: string; legSeconds: number }): Promise<unknown>;
  perfLegStart(a: { runKey: string; legKey: string; tier: string; kind: string; x: number; z: number; spot: string }): Promise<unknown>;
  perfRecordSample(a: Omit<Sample, 'triangles'> & { runKey: string; legKey: string; triangles: bigint; censusJson: string }): Promise<unknown>;
  perfLegEnd(a: Omit<LegSummary, 'legKey' | 'tier' | 'kind' | 'spot' | 'censusTop' | 'trianglesAvg' | 'trianglesMax'> & { legKey: string; trianglesAvg: bigint; trianglesMax: bigint; censusJson: string; extrasJson: string }): Promise<unknown>;
  perfRunEnd(a: { runKey: string; status: string; notes: string }): Promise<unknown>;
  debugTeleport(a: { posX: number; posZ: number }): Promise<unknown>;
};

const JSON_CAP = 20_000;
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const pct = (sorted: number[], q: number): number => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))))] : 0);
const mean = (a: number[]): number => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
const cutJson = (v: unknown): string => { try { const s = JSON.stringify(v) ?? ''; return s.length > JSON_CAP ? s.slice(0, JSON_CAP) : s; } catch { return ''; } };
const g = globalThis as unknown as Record<string, ((...a: unknown[]) => unknown) | undefined>;

export class AtlasPerfTourModule implements IModule {
  readonly name = 'PerfTour';
  private app: App | null = null;
  private status: TourStatus = this.freshStatus();
  private abortRequested = false;
  // sampling state (the UI-phase system fills it while a leg walks)
  private sampling = false;
  private frameTimes: number[] = [];
  private legFrameTimes: number[] = [];
  private lastFrameAt = 0;
  private sampleStartAt = 0;
  private statusEl: HTMLElement | null = null;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasPerfTourClock', Phase.UI, 90, () => this.frame()));
    const lever = Object.assign((opts?: TourOptions) => this.run(opts ?? {}), { status: () => this.status, abort: () => this.abort() });
    (window as unknown as Record<string, unknown>).__perfTour = lever;
    // 2026-09-18: the tour's controls live in the PROBES window (AtlasProbesModule calls buildSection),
    // not in the debug panel — "add the perf tour on that window instead of the debug window".
  }

  dispose(): void {
    this.abort();
    delete (window as unknown as Record<string, unknown>).__perfTour;
  }

  abort(): void { if (this.status.running) { this.abortRequested = true; this.status.phase = 'aborting'; } }

  private freshStatus(): TourStatus {
    return { running: false, runKey: '', label: '', tier: '', leg: '', phase: 'idle', tierIndex: 0, tierCount: 0, legIndex: 0, legCount: 0, samples: 0, lastSample: null, legs: [], error: '', done: false, startedAt: 0 };
  }

  // ── The frame clock: wall-clock frame intervals while a leg walks ──
  private frame(): void {
    const now = performance.now();
    if (this.sampling && this.lastFrameAt > 0) { const ms = now - this.lastFrameAt; this.frameTimes.push(ms); this.legFrameTimes.push(ms); }
    this.lastFrameAt = now;
    if (this.statusEl && (this.frameTimes.length & 7) === 0) this.statusEl.textContent = this.statusLine();
  }

  private statusLine(): string {
    const s = this.status;
    if (!s.running) return s.done ? `done: ${s.legs.length} legs recorded (run ${s.runKey})${s.error ? ` — ${s.error}` : ''}` : (s.error || 'idle');
    const ls = s.lastSample;
    return `${s.phase} · ${s.tier} ${s.tierIndex + 1}/${s.tierCount} · ${s.leg || '-'} ${s.legIndex + 1}/${s.legCount}` + (ls ? ` · ${ls.fps.toFixed(1)} fps, ${ls.frameMsP95.toFixed(0)} ms p95, ${(ls.triangles / 1e6).toFixed(2)} M tris, ${ls.drawCalls} draws` : '');
  }

  // ── The tour card (rendered into the Probes window by AtlasProbesModule) ──
  buildSection(body: HTMLElement): void {
    const note = document.createElement('div');
    note.style.cssText = 'font-size:11px;color:#9cf;margin-bottom:6px';
    note.textContent = 'Every tier: the nearest city, a forest, the shore, a dune desert in a sandstorm — 30 s of walking each, one sample a second into perf_run / perf_leg / perf_sample. Admins only; Commons only. Read it back with npm run verify:perf-tour-report.';
    body.appendChild(note);
    const tiersRow = document.createElement('div');
    tiersRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:6px;font-size:11px;color:#aab';
    const boxes: Array<{ tier: Tier; box: HTMLInputElement }> = [];
    for (const t of TIER_ORDER) {
      const l = document.createElement('label'); const b = document.createElement('input'); b.type = 'checkbox'; b.checked = true; b.dataset.tier = t;
      l.append(b, ` ${TIER_LABEL[t]}`); tiersRow.appendChild(l); boxes.push({ tier: t, box: b });
    }
    body.appendChild(tiersRow);
    const ctl = document.createElement('div');
    ctl.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:6px;font-size:11px;color:#aab';
    const secs = document.createElement('input'); secs.type = 'number'; secs.value = '30'; secs.min = '5'; secs.max = '300'; secs.style.cssText = 'width:60px;background:#141821;color:#dde;border:1px solid #2a2f3a;border-radius:4px;padding:2px 6px';
    const run = document.createElement('button'); run.textContent = 'Run tour'; run.dataset.perfTour = 'run';
    const stop = document.createElement('button'); stop.textContent = 'Abort'; stop.dataset.perfTour = 'abort';
    for (const b of [run, stop]) b.style.cssText = 'background:#243046;color:#dde;border:1px solid #3a4a66;border-radius:4px;padding:2px 8px;cursor:pointer';
    ctl.append('Seconds per leg', secs, run, stop);
    body.appendChild(ctl);
    const st = document.createElement('div'); st.style.cssText = 'font-size:11px;color:#dde;min-height:14px'; st.textContent = this.statusLine();
    body.appendChild(st); this.statusEl = st;
    run.onclick = () => { void this.run({ tiers: boxes.filter((b) => b.box.checked).map((b) => b.tier), legSeconds: Number(secs.value) || 30, label: 'debug-menu' }).catch(() => { /* reported in status */ }); };
    stop.onclick = () => this.abort();
  }

  // ── The tour ──
  async run(opts: TourOptions): Promise<TourStatus> {
    if (this.status.running) throw new Error('perf tour already running');
    const app = this.app; const conn = atlas.conn as unknown as { reducers: Reducers } | null;
    if (!app || !conn) throw new Error('not connected');
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId) throw new Error('the perf tour walks the Commons — Tab there first');
    const post = app.getModule<PostModule>('Post'); const input = app.getModule<AtlasInputModule>('AtlasInput'); const weather = app.getModule<WeatherModule>('Weather');
    const env = app.getModule<EnvironmentModule>('Environment'); const settlements = app.getModule<CommonsSettlementsModule>('CommonsSettlements'); const debug = app.getModule<AtlasDebugMenuModule>('DebugMenu');
    if (!post || !input || !weather || !env || !settlements || !debug) throw new Error('perf tour: a module is missing');
    const tiers = (opts.tiers?.length ? opts.tiers : [...TIER_ORDER]).filter((t) => TIER_ORDER.includes(t));
    const legs = (opts.legs?.length ? opts.legs : [...LEG_KINDS]).filter((l) => LEG_KINDS.includes(l));
    const legSeconds = Math.max(3, Math.min(600, opts.legSeconds ?? 30));
    const settleMs = opts.settleMs ?? 8000; const loadMaxMs = opts.loadMaxMs ?? 60_000; const turn = opts.turnRadPerS ?? 0.15;
    const runKey = `pt-${Date.now().toString(36)}-${Math.floor(Math.random() * 46656).toString(36)}`;
    const backend = (app.renderer.native as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend ? 'webgpu' : 'webgl2';
    const s: TourStatus = { ...this.freshStatus(), running: true, runKey, label: opts.label ?? 'lever', tierCount: tiers.length, legCount: legs.length, startedAt: Date.now(), phase: 'starting' };
    this.status = s;
    this.abortRequested = false;
    const originalTier = activeTier(); const originalWeather = weather.debugWeather;
    // THE PINNED-TIER LAW (run 1, 2026-09-18): the Graphics menu's auto-tune steps the PRESET down at
    // < 42 fps for 3 s (20 s cooldown) through the same setTier the tour uses, and the FSR governor
    // moves the render scale — every settlement leg dragged the legs after it down the ladder (the
    // "ultra" desert walked at potato's foliage density). Both are pinned OFF for the tour and restored.
    const originalAutoTune = activePreset().autoTune ?? false;
    let originalAdaptive: string | null = null;
    try { originalAdaptive = localStorage.getItem('atlas:adaptive'); localStorage.setItem('atlas:adaptive', 'off'); } catch { /* no storage */ }
    if (originalAutoTune) setAutoTune(false);
    const spots = this.resolveSpots(debug, legs);
    try {
      await conn.reducers.perfRunStart({ runKey, label: s.label, backend, userAgent: navigator.userAgent, tiers: JSON.stringify(tiers), legSeconds });
      for (let ti = 0; ti < tiers.length; ti++) {
        const tier = tiers[ti]; s.tier = tier; s.tierIndex = ti;
        this.checkAbort();
        s.phase = 'switching tier'; post.setTier(tier); await sleep(settleMs);
        for (let li = 0; li < legs.length; li++) {
          const kind = legs[li]; s.leg = kind; s.legIndex = li; s.samples = 0; s.lastSample = null;
          this.checkAbort();
          const legKey = `${runKey}-${tier}-${kind}`;
          const spot = spots[kind];
          weather.setDebugWeather(kind === 'desert_storm' ? 'sandstorm' : 'clear');
          if (!spot) {
            await conn.reducers.perfLegStart({ runKey, legKey, tier, kind, x: 0, z: 0, spot: '' });
            await conn.reducers.perfLegEnd({ ...this.emptySummary(), legKey, status: 'skipped', notes: `no ${kind} spot found`, censusJson: '', extrasJson: '' });
            s.legs.push({ ...this.emptyLeg(legKey, tier, kind, ''), status: 'skipped', notes: `no ${kind} spot found` });
            continue;
          }
          s.phase = 'teleporting';
          await conn.reducers.debugTeleport({ posX: spot.x, posZ: spot.z });
          const arrived = await this.waitFor(() => Math.hypot(atlas.localX - spot.x, atlas.localZ - spot.z) < 40, 25_000);
          s.phase = 'loading';
          await sleep(2500);
          const loaded = await this.waitFor(() => { const e = env.loadReadiness(); const st = settlements.loadReadiness(); return e.chunksPending === 0 && e.foliagePending === 0 && (!st.resolved || st.assembling === 0); }, loadMaxMs);
          await conn.reducers.perfLegStart({ runKey, legKey, tier, kind, x: spot.x, z: spot.z, spot: spot.label });
          // ── the walk ──
          s.phase = 'walking';
          const legStart = performance.now(); const samples: Sample[] = []; const sampleCensus: Array<string> = [];
          this.frameTimes = []; this.legFrameTimes = []; this.lastFrameAt = 0; this.sampling = true; this.sampleStartAt = legStart;
          input.setAutoWalk(1, 0, turn);
          try {
            let sec = 0;
            while (performance.now() - legStart < legSeconds * 1000) {
              await sleep(1000);
              this.checkAbort();
              sec++;
              const sample = this.takeSample(sec, app);
              samples.push(sample); s.samples = samples.length; s.lastSample = sample;
              const census = sec % 5 === 0 ? cutJson(g.__triangleCensus?.(10)) : '';
              sampleCensus.push(census);
              void conn.reducers.perfRecordSample({ ...sample, runKey, legKey, triangles: BigInt(Math.round(sample.triangles)), censusJson: census }).catch(() => { /* a lost sample never stalls the walk */ });
            }
          } finally { input.setAutoWalk(0, 0, 0); this.sampling = false; }
          // ── the summary ──
          s.phase = 'summarising';
          const summary = this.summarise(legKey, tier, kind, spot.label, samples, this.legFrameTimes, legStart);
          summary.notes = `${arrived ? 'arrived' : 'NOT arrived'}; ${loaded ? 'loaded' : 'load wait capped'}; live ${activeTier()} (foliage ${activePreset().foliageDensityScale ?? 1})`;
          const census = g.__triangleCensus?.(14) as { total?: number; top?: Array<{ name: string; tris: number }> } | undefined;
          summary.censusTop = (census?.top ?? []).map((r) => ({ name: r.name, tris: r.tris }));
          const extras = {
            terrain: env.loadReadiness(), settlements: this.settlementDigest(), foliage: g.__foliageCensus?.(), pipelines: g.__pipelines?.(),
            draws: (g.__drawCensus?.() as { rows?: unknown[] } | unknown[] | undefined), weather: (g.__weather?.() as { kind?: string } | undefined)?.kind ?? null, tier, censusTotal: census?.total ?? null,
          };
          const drawsRows = Array.isArray(extras.draws) ? extras.draws.slice(0, 12) : (extras.draws as { rows?: unknown[] } | undefined)?.rows?.slice(0, 12);
          await conn.reducers.perfLegEnd({
            legKey, status: 'done', notes: summary.notes, samples: summary.samples, fpsAvg: summary.fpsAvg, fpsP05: summary.fpsP05, frameMsP50: summary.frameMsP50, frameMsP95: summary.frameMsP95,
            frameMsP99: summary.frameMsP99, frameMsMax: summary.frameMsMax, drawCallsAvg: summary.drawCallsAvg, trianglesAvg: BigInt(Math.round(summary.trianglesAvg)), trianglesMax: BigInt(Math.round(summary.trianglesMax)),
            pipelines: summary.pipelines, heapMbAvg: summary.heapMbAvg, heapMbMax: summary.heapMbMax, loafCount: summary.loafCount, loafMsTotal: summary.loafMsTotal, gpuMsAvg: summary.gpuMsAvg,
            censusJson: cutJson(census ?? null), extrasJson: cutJson({ ...extras, draws: drawsRows ?? null }),
          }).catch((e: Error) => { summary.notes += `; legEnd failed: ${e.message}`; });
          s.legs.push(summary);
        }
      }
      s.phase = 'done'; s.done = true;
      await conn.reducers.perfRunEnd({ runKey, status: 'done', notes: `${s.legs.length} legs` }).catch(() => { /* recorded legs stand */ });
    } catch (e) {
      s.error = String((e as Error)?.message ?? e); s.phase = 'aborted';
      this.sampling = false; input.setAutoWalk(0, 0, 0);
      await conn.reducers.perfRunEnd({ runKey, status: 'aborted', notes: s.error.slice(0, 300) }).catch(() => { /* best effort */ });
    } finally {
      s.running = false;
      weather.setDebugWeather(originalWeather);
      if (originalAutoTune) setAutoTune(true);
      try { if (originalAdaptive === null) localStorage.removeItem('atlas:adaptive'); else localStorage.setItem('atlas:adaptive', originalAdaptive); } catch { /* no storage */ }
      if (activeTier() !== originalTier) post.setTier(originalTier);
      if (this.statusEl) this.statusEl.textContent = this.statusLine();
    }
    return s;
  }

  private checkAbort(): void { if (this.abortRequested) throw new Error('aborted'); }

  private async waitFor(cond: () => boolean, maxMs: number): Promise<boolean> {
    const t0 = performance.now();
    while (performance.now() - t0 < maxMs) { if (cond()) return true; this.checkAbort(); await sleep(500); }
    return cond();
  }

  /** The four places, resolved once from where the tour starts. */
  private resolveSpots(debug: AtlasDebugMenuModule, legs: readonly LegKind[]): Partial<Record<LegKind, Spot>> {
    const out: Partial<Record<LegKind, Spot>> = {};
    for (const kind of legs) {
      if (kind === 'settlement') {
        const conn = atlas.conn; const commons = conn ? [...conn.db.world.iter()].find((w) => w.kind === 'commons') : null;
        if (!commons) continue;
        const sites = settlementSites(commons.terrainSeed).filter((si) => si.tier === 'city');
        let best: { x: number; z: number; d: number; id: number } | null = null;
        for (const si of sites) { const d = Math.hypot(si.centerX - atlas.localX, si.centerZ - atlas.localZ); if (!best || d < best.d) best = { x: si.centerX, z: si.centerZ, d, id: si.id }; }
        if (best) out.settlement = { x: best.x, z: best.z, label: `city #${best.id} centre (${(best.d / 1000).toFixed(1)} km from the start)` };
        continue;
      }
      const f = debug.findFeatureSpot(LEG_FEATURE[kind], 'nearest');
      if (typeof f === 'string') continue;
      const spot = f as FeatureSpot;
      out[kind] = { x: spot.x, z: spot.z, label: spot.label };
    }
    return out;
  }

  private takeSample(sec: number, app: App): Sample {
    const frames = this.frameTimes; this.frameTimes = [];
    const sorted = [...frames].sort((a, b) => a - b);
    const elapsedS = Math.max(0.25, (performance.now() - (this.sampleStartAt + (sec - 1) * 1000)) / 1000);
    const pipe = (g.__pipelines?.() as { pipelines?: number | null; calls?: number | null; triangles?: number | null } | undefined) ?? {};
    const native = app.renderer.native as unknown as { info?: { render?: { calls?: number; drawCalls?: number; triangles?: number; timestamp?: number } } };
    const heap = (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory?.usedJSHeapSize;
    const windowStart = performance.now() - 1000;
    const loafs = (g.__loaf?.(600) as Array<{ t: number; dur: number }> | undefined) ?? [];
    return {
      t: Math.round((performance.now() - this.sampleStartAt) / 100) / 10,
      fps: frames.length / elapsedS,
      frameMsP50: pct(sorted, 0.5), frameMsP95: pct(sorted, 0.95), frameMsMax: sorted.length ? sorted[sorted.length - 1] : 0,
      // PER-FRAME draw calls. three's `info.render.calls` is the CUMULATIVE number of render() calls since
      // the app started (run 1 recorded it: 10k → 245k across the tour); `drawCalls` is the current frame's.
      drawCalls: Math.round(native.info?.render?.drawCalls ?? 0),
      triangles: Math.round(pipe.triangles ?? native.info?.render?.triangles ?? 0),
      pipelines: Math.round(pipe.pipelines ?? 0),
      heapMb: heap ? heap / 1048576 : 0,
      gpuMs: Number(native.info?.render?.timestamp ?? 0) || 0,
      loaf: loafs.filter((l) => l.t >= windowStart).length,
      x: atlas.localX, z: atlas.localZ,
    };
  }

  private summarise(legKey: string, tier: Tier, kind: LegKind, spot: string, samples: Sample[], frames: number[], legStart: number): LegSummary {
    const sorted = [...frames].sort((a, b) => a - b);
    const fps = samples.map((s) => s.fps).sort((a, b) => a - b);
    const loafs = ((g.__loaf?.(600) as Array<{ t: number; dur: number }> | undefined) ?? []).filter((l) => l.t >= legStart); // the ring holds 600 (run 1 read the 30-entry default and capped every busy leg at 30)
    return {
      legKey, tier, kind, spot, status: 'done', notes: '', samples: samples.length,
      fpsAvg: mean(samples.map((s) => s.fps)), fpsP05: pct(fps, 0.05),
      frameMsP50: pct(sorted, 0.5), frameMsP95: pct(sorted, 0.95), frameMsP99: pct(sorted, 0.99), frameMsMax: sorted.length ? sorted[sorted.length - 1] : 0,
      drawCallsAvg: Math.round(mean(samples.map((s) => s.drawCalls))), trianglesAvg: mean(samples.map((s) => s.triangles)), trianglesMax: Math.max(0, ...samples.map((s) => s.triangles)),
      pipelines: samples.length ? samples[samples.length - 1].pipelines : 0, heapMbAvg: mean(samples.map((s) => s.heapMb)), heapMbMax: Math.max(0, ...samples.map((s) => s.heapMb)),
      loafCount: loafs.length, loafMsTotal: loafs.reduce((a, l) => a + l.dur, 0), gpuMsAvg: mean(samples.map((s) => s.gpuMs).filter((v) => v > 0)), censusTop: [],
    };
  }

  private emptyLeg(legKey: string, tier: Tier, kind: LegKind, spot: string): LegSummary {
    return { legKey, tier, kind, spot, status: 'skipped', notes: '', samples: 0, fpsAvg: 0, fpsP05: 0, frameMsP50: 0, frameMsP95: 0, frameMsP99: 0, frameMsMax: 0, drawCallsAvg: 0, trianglesAvg: 0, trianglesMax: 0, pipelines: 0, heapMbAvg: 0, heapMbMax: 0, loafCount: 0, loafMsTotal: 0, gpuMsAvg: 0, censusTop: [] };
  }
  private emptySummary() {
    return { status: 'skipped', notes: '', samples: 0, fpsAvg: 0, fpsP05: 0, frameMsP50: 0, frameMsP95: 0, frameMsP99: 0, frameMsMax: 0, drawCallsAvg: 0, trianglesAvg: 0n, trianglesMax: 0n, pipelines: 0, heapMbAvg: 0, heapMbMax: 0, loafCount: 0, loafMsTotal: 0, gpuMsAvg: 0 };
  }

  private settlementDigest(): unknown {
    try {
      const d = g.__settlements?.() as Record<string, unknown> | undefined; if (!d) return null;
      const keep: Record<string, unknown> = {};
      for (const k of ['sites', 'near', 'built', 'assembling', 'lodHist', 'refine', 'colliders', 'inRange']) if (k in d) keep[k] = d[k];
      return keep;
    } catch { return null; }
  }
}
